import {
	ArchModificationOptions,
	ExtensionThicknessValue,
	Forefoot,
	ForefootPostValue,
	LROptions,
	LRStrictOptions,
	MedLowHigh,
	RearfootPost,
	ShellModifications
} from '@/store/prescription/prescription-types';
import {
	ExtendedLRSelectItem,
	LRRadio,
	LRRadioWithoutNone,
	LRSelectItem,
	LRStrictRadio,
	MortonsThickness,
	RadioItem,
	RadioSelectExtendedItem,
	RadioSelectItem,
	SelectItem,
	TypedFactory
} from '@/store/prescription/definition/categories/prescriptionCategoriesDefinition';

const medLowHigh = { low: 'Low 6mm', medium: 'Medium 12mm', high: 'High 18mm', 'x-high': 'X-High 21mm' };

const shellRigidity: RadioSelectItem<ShellModifications['shellRigidity'], NonNullable<ShellModifications['shellRigidityOption']>> = {
	display: 'Shell Rigidity',
	description:
		'Rigidity is determined by patient weight, shell thickness, arch height and foot mobility. We recommend Semi-Rigid for most patients. For heavier patients, patients with low arches, or for very flexible foot types we recommend Rigid. Flexible should be used for patients that will not tolerate a Rigid orthotic or do not require a high degree of correction. EVA should be used for an accommodative device only.',
	imgPath: '/assets/images/prescriptions/Shell Rigidity.svg',
	radioValues: { flexible: 'Flexible', semi: 'Semi-Rigid', rigid: 'Rigid', 'super-flex': 'EVA' },
	selectValues: { 'very-soft': 'Very soft', 'semi-soft': 'Semi-Soft', soft: 'Soft', 'semi-hard': 'Semi hard', hard: 'Hard', 'very-hard': 'Very hard' }
};

const shellLength: RadioItem<ShellModifications['shellLength']> = {
	display: 'Shell Length',
	description:
		'<b>Met Length</b>- A standard shell length from heel to met heads.\n' +
		'<b>Sulcus Length</b> - Shell length is from the heel to the sulcus. The shell is flexible from the met heads to the sulcus. Scrim not available\n' +
		'<b>Full Length</b> - From heel to full length. The shell is flexible from met heads to toes. Scrim not available',
	imgPath: '/assets/images/prescriptions/Shell_Length.svg',
	radioValues: { met: 'Met Length', sulcus: 'Sulcus Length', full: 'Full Length' }
};

const shellShape: SelectItem<ShellModifications['shellShape']> = {
	display: 'Shell Shape',
	description:
		'<b>Standard</b> – Our default shell design\n' +
		'<b>Hourglass</b> – Curves in the medial and lateral borders to create an hourglass shape. Recommended for narrow and curved last shoes\n' +
		'<b>Medial Deformity</b> – Recommended for patients with a midfoot deformity, like PTTD or pes planus\n' +
		'<b>Lateral Deformity</b> – Recommended for patients with a prominent base of 5th',
	imgPath: '/assets/images/prescriptions/Shell_Shape.svg',
	selectValues: { standard: 'Standard', hourglass: 'Hourglass', 'medial-deformity': 'Medial Deformity', 'lateral-deformity': 'Lateral Deformity' }
};

const firstRayCutout: RadioSelectItem<LROptions, NonNullable<ShellModifications['firstRayCutout']['value']>> = {
	description:
		'The first ray cut out removes the plastic shell under the medial distal edge of the orthosis as shown in the area in gray. This is will allow the first ray to extend its range of plantarflexion. Choose 45 degrees for a standard cutout, or choose 60 degrees for a deeper cutout.',
	display: '1st Ray Cut Out',
	imgPath: '/assets/images/prescriptions/1st Ray Cut out.svg',
	radioValues: { ...LRRadio },
	selectValues: { '45deg': '45 degrees', '60deg': '60 degrees' }
};

const heelCutout: RadioItem<LROptions> = {
	description:
		'Can be used to either reduce bulk in a shallow shoe, like a dress shoe, or to accommodate heel pain from a spur or plantar fasciitis. The standard heel cutout is 1”. The cutout does not include a fill.',
	display: 'Heel Cut Out',
	imgPath: '/assets/images/prescriptions/heelCutout.svg',
	radioValues: {
		...LRRadio
	}
};

const cutoutAndStyloid: RadioItem<LROptions> = {
	description: 'A circular-shaped cutout on the lateral aspect of the shell to reduce pressure of a prominent 5th meta base',
	display: 'Cutout at Styloid Process',
	imgPath: '/assets/images/prescriptions/cutout_at_styloid_process.svg',
	radioValues: {
		...LRRadio
	}
};

const cutoutAndFirst: RadioItem<LROptions> = {
	description: 'A cutout at the 1st similar to a J-shaped cutout.',
	display: 'Cutout at 1st Met Head',
	imgPath: '/assets/images/prescriptions/cutout_at_1st_met_head.svg',
	radioValues: {
		...LRRadio
	}
};

const cutoutFifth: RadioItem<LROptions> = {
	description: 'A circular-shaped cutout at the 5th met head to reduce pressure and remove bulk.',
	display: 'Cutout 5th Met Head',
	imgPath: '/assets/images/prescriptions/cutout_at_5th_met_head.svg',
	radioValues: {
		...LRRadio
	}
};

const navicularWell: RadioItem<LROptions> = {
	description: 'A circular-shaped well to offload pressure from a prominent navicular bone.',
	display: 'Navicular Well',
	imgPath: '/assets/images/prescriptions/Navicular_well.svg',
	radioValues: {
		...LRRadio
	}
};

const wellAndStyloid: RadioItem<LROptions> = {
	description: 'A circular-shaped well to offload pressure from a prominent 5th meta base.',
	display: 'Well at Styloid Process',
	imgPath: '/assets/images/prescriptions/Well_at_styloid_process.svg',
	radioValues: {
		...LRRadio
	}
};

const reverseMortonsExtension: RadioSelectExtendedItem<
	NonNullable<ShellModifications['reverseMortonsExtension']['type']>,
	NonNullable<ShellModifications['reverseMortonsExtension']['value']>,
	NonNullable<ShellModifications['reverseMortonsExtension']['side']>
> = {
	description:
		"The reverse Morton's extension is an semi-rigid extension distally of the shell under the 2-5 MPJ extending to the sulcus. This extension is flexible so it will not impede the extension of the MPJ’s or propulsion of the foot. It is used to offload and increase range of motion of the 1st MTP.\n" +
		'The extension will be made as the same thickness as the overall shell thickness by default, with a max thickness of 4mm. If you would like a thinner extension then select “2.5 mm shell thickness.”',
	display: "Reverse Morton's Ext",
	imgPath: "/assets/images/prescriptions/Reverse Morton's Ext.svg",
	radioValues: {
		none: 'None',
		flexible: 'Flexible',
		'semi-rigid': 'Semi-Rigid'
	},
	selectValues: {
		'2.5mm': '2.5mm',
		consistent: 'Consistent Thickness'
	},
	secondSelectValues: {
		...LRRadioWithoutNone
	}
};

const heelSeat: RadioItem<ShellModifications['heelSeat']> = {
	description:
		'Our default is 12mm which is approximately 1/2” Select lower heel seat heights for dress shoes or shoes with very low heel counters. Select a deeper heel seath for increased correction is required, or when a heel skive or heel padding is prescribed.',
	display: 'Heel Seat',
	imgPath: '/assets/images/prescriptions/Heel Seat.svg',
	radioValues: {
		'3': '3mm',
		'6': '6mm',
		'9': '9mm',
		'12': '12mm',
		'15': '15mm',
		'18': '18mm',
		'21': '21mm'
	}
};

const archModification: LRSelectItem<ArchModificationOptions> = {
	description:
		'Increasing or decreasing the arch height will have a correlative impact on the degree of correction and shell confirmation to the anatomy. Treatment objectives and patient tolerance should be considered when selecting these options. For patients with hypermobility and low medial arches, increasing the height of the medial arch will decrease the flexibility of the orthotic in this area and may offer more control.',
	display: 'Arch Modification',
	imgPath: '/assets/images/prescriptions/Arch_Modification_revised.svg',
	LR: {
		none: 'N/A',
		'increase-3': 'Increase 3mm',
		'increase-6': 'Increase 6mm',
		'increase-12': 'Increase 12mm',
		'decrease-3': 'Decrease 3mm',
		'decrease-6': 'Decrease 6mm'
	}
};

const archReinforcement: RadioSelectExtendedItem<
	LROptions,
	NonNullable<ShellModifications['archReinforcement']['value']>,
	NonNullable<ShellModifications['archReinforcement']['pattern']>
> = {
	radioValues: {
		...LRRadio
	},
	selectValues: {
		'1.5mm': '1.5mm',
		'3mm': '3mm',
		'6mm': '6mm'
	},
	secondSelectValues: {
		honeycomb: 'Honeycomb',
		filled: 'Filled'
	},
	description: `Increases the thickness of the shell under the
arch to reduce flexion and maintain desired
control.
Choose from two different styles: Filled or
Honeycomb.`,
	display: 'Arch Reinforcement',
	imgPath: '/assets/images/prescriptions/Arch_Reinforcement.svg'
};

const mortonsExtension: RadioSelectExtendedItem<
	NonNullable<ShellModifications['mortonsExtension']['type']>,
	NonNullable<ShellModifications['mortonsExtension']['value']>,
	NonNullable<ShellModifications['mortonsExtension']['side']>
> = {
	radioValues: {
		none: 'None',
		flexible: 'Flexible',
		'semi-rigid': 'Semi-Rigid',
		rigid: 'Rigid'
	},
	selectValues: {
		'2.5mm': '2.5mm',
		consistent: 'Consistent Thickness'
	},
	secondSelectValues: {
		...LRRadioWithoutNone
	},
	description:
		'The Morton’s Extension is an extrinsic extension of the shell under the 1st MPJ. It is used to stabilize and decelerate the 1st ray during gait. A Morton’s Extension will add a small amount of bulk to the device.',
	display: 'Mortons Extension',
	imgPath: "/assets/images/prescriptions/Flexible Morton's Ext.svg"
};

const medialFlange: RadioSelectItem<LROptions, MedLowHigh> = {
	description:
		'Medial flanges are used to increase correction by extending the height of the medial border of the orthosis. This modification will widen the overall profile of the orthosis and appropriate footwear should be recommended.',
	display: 'Medial Flange',
	imgPath: '/assets/images/prescriptions/Medial Flange.svg',
	radioValues: { ...LRRadio },
	selectValues: { ...medLowHigh }
};

const lateralFlange: RadioSelectItem<LROptions, MedLowHigh> = {
	description:
		'A lateral flange increases the height of the lateral border of the orthosis. This modification will widen the overall profile of the orthosis and appropriate should be recommended. This modification is used to control abduction of the forefoot.',
	display: 'Lateral Flange',
	imgPath: '/assets/images/prescriptions/Lateral Flange.svg',
	radioValues: { ...LRRadio },
	selectValues: { ...medLowHigh }
};

const shellSpecialRX: [] = [];

const typeFactory = new TypedFactory<ShellModifications>();
export default typeFactory.typeItems({
	shellRigidity,
	shellLength,
	shellShape,
	firstRayCutout,
	reverseMortonsExtension,
	heelSeat,
	archModification,
	medialFlange,
	lateralFlange,
	heelCutout,
	shellSpecialRX,
	archReinforcement,
	cutoutAndStyloid,
	cutoutAndFirst,
	cutoutFifth,
	navicularWell,
	wellAndStyloid,
	mortonsExtension
});
